import throttledAnimationFrame from './utils/throttledAnimationFrame.js';
import { stopStream, stopTrack, getZoomTracks } from './utils/StreamHelpers.js';
import Logger from './Logger.js';

const TrackProcessorSupport =
  typeof window.MediaStreamTrackProcessor === 'function';

class ZoomMixer {
  constructor(name = 'global') {
    Logger.info('ZoomMixer::constructor', name);
    this.name = name;
    this.zoom = null;
    this.canvas = null;
    this.ctx = null;
    this.originalStream = null;
    this.outStream = null;
    this.video = null;
    this.reader = null;
    this.paused = false;
    this.raf = throttledAnimationFrame(this.drawVideo.bind(this), 30);
    this.abort = false;
  }

  setZoom(zoom) {
    Logger.debug('ZoomMixer::setZoom', this.name, zoom);
    if (!(zoom === null || typeof zoom === 'number')) {
      throw new TypeError('Zoom MUST be number or null');
    }
    this.zoom = zoom === null ? null : parseFloat(zoom);
  }

  initiateStream(userMediaStream) {
    Logger.info('ZoomMixer::initiate', this.name);
    const videoTracks = userMediaStream.getVideoTracks();
    if (videoTracks.length === 0) {
      Logger.warn('ZoomMixer::initiate', this.name, 'missing track');
      return userMediaStream;
    }
    const [videoTrack] = videoTracks;
    const trackSettings = videoTrack.getSettings();
    if (
      videoTrack.readyState !== 'live' ||
      !trackSettings.width ||
      !trackSettings.height
    ) {
      Logger.warn(
        'ZoomMixer::initiate',
        this.name,
        '0 width or height, or not ready',
        videoTrack.readyState,
        trackSettings.width,
        trackSettings.height
      );
      return userMediaStream;
    }
    if (this.originalStream) {
      Logger.warn(
        'ZoomMixer::initiate',
        this.name,
        'existing originalStream!',
        this.originalStream.id,
        userMediaStream.id
      );
      if (this.originalStream.id !== userMediaStream.id) {
        this.stopOriginalStream();
      }
      this.terminate();
    }
    this.originalStream = userMediaStream;
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'eyeson-zoom-stream';
    this.ctx = this.canvas.getContext('2d', { desynchronized: true });
    this.ctx.imageSmoothingEnabled = true;
    // this.ctx.imageSmoothingQuality = 'low';
    this.canvas.width = trackSettings.width;
    this.canvas.height = trackSettings.height;
    const canvasStream = this.canvas.captureStream();
    const [canvasTrack] = canvasStream.getVideoTracks();
    if (!canvasTrack.canvas) {
      canvasTrack.type = 'canvas-track';
      canvasTrack.canvas = this.canvas;
    }
    const settings = {};
    if (typeof canvasTrack.getSettings === 'function') {
      Object.assign(settings, canvasTrack.getSettings());
    }
    Object.assign(settings, trackSettings);
    canvasTrack.getSettings = () => settings;
    canvasTrack.srcLabel = videoTrack.label;
    canvasTrack.srcDeviceId = trackSettings.deviceId;
    this.outStream = new MediaStream([canvasTrack]);
    userMediaStream
      .getAudioTracks()
      .forEach(track => this.outStream.addTrack(track));
    this.start();
    return this.outStream;
  }

  start() {
    Logger.info('ZoomMixer::start', this.name);
    let video = null;
    const { width, height } = this.canvas;
    if (!width || !height) {
      Logger.warn(
        'ZoomMixer::start 0 width or height',
        this.name,
        width,
        height
      );
      return;
    }
    if (TrackProcessorSupport) {
      try {
        const [track] = this.originalStream.getVideoTracks();
        const processor = new MediaStreamTrackProcessor(track);
        this.reader = processor.readable.getReader();
        // eslint-disable-next-line no-empty, no-unused-vars
      } catch (error) {}
    }
    if (!this.reader) {
      video = document.createElement('video');
      video.playsInline = true;
      video.muted = true;
      video.width = width;
      video.height = height;
      video.srcObject = this.originalStream;
      this.video = video;
    }
    this.abort = false;
    this.paused = false;
    if (this.reader) {
      Logger.info('ZoomMixer::start::videoTrackProcessor', this.name);
      this.raf.requestAnimationFrame();
    } else {
      video.onloadeddata = () => {
        Logger.info('ZoomMixer::start::video.loadeddata', this.name);
        this.raf.requestAnimationFrame();
      };
      this.playPromise = video.play();
      this.playPromise.catch(error => {
        Logger.error('ZoomMixer::start::video.play', this.name, error);
      });
    }
  }

  async drawVideo() {
    let frame = null;
    if (this.abort) {
      return;
    }
    if (this.reader) {
      try {
        const { value } = await this.reader.read();
        if (value) {
          value.width = value.displayWidth;
          value.height = value.displayHeight;
          frame = value;
        }
        // eslint-disable-next-line no-empty, no-unused-vars
      } catch (error) {}
    }
    if (!frame && this.video) {
      const { video } = this;
      frame = video;
      frame.width = video.videoWidth;
      frame.height = video.videoHeight;
    }
    if (this.abort) {
      return;
    }
    if (frame && frame.width > 0 && frame.height > 0) {
      const { width, height } = frame;
      const { zoom, canvas } = this;
      if (width !== canvas.width || height !== canvas.height) {
        canvas.width = width;
        canvas.height = height;
      }
      if (!this.paused && zoom) {
        const cropWidth = width / zoom;
        const cropHeight = height / zoom;
        const cropX = (width - cropWidth) / 2;
        const cropY = (height - cropHeight) / 2;
        this.ctx.drawImage(
          frame,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );
      } else {
        this.ctx.drawImage(frame, 0, 0);
      }
    }
    if (frame && typeof frame.close === 'function') {
      frame.close();
    }
    if (!this.abort) {
      this.raf.requestAnimationFrame();
    }
  }

  suspend() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
  }

  stop() {
    Logger.info('ZoomMixer::stop', this.name);
    this.abort = true;
    if (this.raf) {
      this.raf.cancelAnimationFrame();
    }
    if (this.video) {
      const { video, playPromise } = this;
      if (playPromise && video) {
        playPromise.then(() => video.pause());
      }
      this.video.onloadeddata = null;
      this.video = null;
      this.playPromise = null;
    }
    if (this.reader) {
      this.reader = null;
    }
  }

  stopOriginalStream() {
    Logger.info(
      'ZoomMixer::stopOriginalStream',
      this.name,
      this.originalStream
    );
    this.stop();
    stopStream(this.originalStream);
  }

  terminate() {
    Logger.info('ZoomMixer::terminate', this.name);
    this.stop();
    if (this.outStream) {
      getZoomTracks(this.outStream).forEach(track => {
        stopTrack(track);
        Logger.info('ZoomMixer::terminate stop track', this.name, track);
      });
    }
    this.originalStream = null;
    this.outStream = null;
    this.canvas = null;
    this.ctx = null;
    this.video = null;
  }

  destroy() {
    Logger.info('ZoomMixer::destroy', this.name);
  }
}

export default ZoomMixer;
